import { createFileRoute, Link } from '@tanstack/react-router'
import { contentWrapper } from '../../../../../styles/contentWrapper'
import { pageTitle, sectionTitle, sectionWrapper, titleWrapper } from '../../../../../styles/sectionStyles'
import ArrowBack from '~icons/material-symbols/arrow-back.svg'
import OpenInNew from '~icons/material-symbols/open-in-new.svg'
import { textLabel, text, textWithLabelWrapper } from '../../../../../styles/textStyles'
import { css } from '../../../../../../styled-system/css'
import { button } from '../../../../../styles/buttonStyles'
import { useGetMyCompanies } from '../../../../../libs/admin/hooks'
import { LoadingOverlay } from '../../../../../components/loading-overlay'
import { IssueStoreAccountSection } from './-component/IssueStoreAccountSection'

export const Route = createFileRoute('/admin/companies/$companyId/$storeId/')({
  component: RouteComponent,
})

const useStoreData = () => {
  const { companyId, storeId } = Route.useParams()
  const { data: myCompanies, isLoading, isFetching, isError, dataUpdatedAt } = useGetMyCompanies()
  if (isLoading) {
    return { isLoading }
  }
  if (isError) {
    return { isError }
  }
  if (!companyId || !storeId) {
    throw new Error('Company ID or Store ID is missing')
  }
  if (!myCompanies) {
    throw new Error('My companies data not found')
  }
  const company = myCompanies.find((company) => company.company?.id === companyId)
  if (!company) {
    if (isFetching) {
      // 新規作成時には事業者情報がない場合があるため、isFetchingでもローディング状態を返す
      return { isLoading: true }
    }
    throw new Error('Company not found')
  }
  const store = company.stores.find((store) => store.id === storeId)
  if (!store) {
    if (isFetching) {
      // 新規作成時には店舗情報がない場合があるため、isFetchingでもローディング状態を返す
      return { isLoading: true }
    }
    throw new Error('Store not found')
  }
  return {
    companyId,
    storeId,
    company,
    store,
    isLoading,
    isError,
    dataUpdatedAt,
  }
}

function RouteComponent() {
  return <Layout />
}

const Layout = () => {
  const { isLoading, isError } = useStoreData()
  if (isLoading) {
    return (
      <div className={contentWrapper()}>
        <LoadingOverlay />
      </div>
    )
  }
  if (isError) {
    console.error("Error loading store data")
    return null
  }

  return (
    <div className={contentWrapper()}>
      <div className={titleWrapper()}>
        <div className={pageTitle()}>
          <Link to="..">
            <ArrowBack />
          </Link>
          店舗情報
        </div>
      </div>
      <div className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      })}>
        <BreadCrumb />
        <div className={css({
          display: 'flex',
          flexDirection: 'row',
          gap: '8px',
          width: '100%',
        })}>
          <IconPane />
          <StoreInfoPane />
        </div>
        <StoreAccountSection />
      </div>
    </div>
  )
}

const StoreAccountSection = () => {
  const { storeId, store, isLoading, isError } = useStoreData()
  return (
    <IssueStoreAccountSection
      storeId={storeId}
      store={store}
      isLoading={isLoading}
      isError={isError}
    />
  )
}

const BreadCrumb = () => {
  const { company, isLoading, isError } = useStoreData()
  if (isLoading) {
    return null
  }
  if (isError) {
    console.error("Error loading company data")
    return null
  }
  if (!company || !company.company) {
    return null
  }
  return (
    <div className={css({
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    })}>
      <Link
        to="/admin/companies/$companyId"
        params={{ companyId: company.company.id }}
      >
        <span className={css({
          color: 'text.accentPrimary',
          fontWeight: '400',
        })}>
          {company.company.name}
        </span>
      </Link>
      <span className={css({
        color: 'text.tertiary',
      })}>/</span>
      <span className={css({
        color: 'text.tertiary',
      })}>店舗情報</span>
    </div>
  )
}

const IconPane = () => {
  const { store, isLoading, isError, dataUpdatedAt } = useStoreData()
  if (isLoading) {
    return null
  }
  if (isError) {
    console.error("Error loading store data")
    return null
  }
  if (!store) {
    return null
  }
  const iconImageUrl = store.iconUrl
  return (
    <div className={css(
      sectionWrapper.raw(),
      {
        maxWidth: '300px',
      },
    )}>
      <div className={sectionTitle()}>店舗アイコン画像</div>
      <div className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        width: '100%',
      })}>
        {iconImageUrl ? (
          <img
            src={`${iconImageUrl}?v=${dataUpdatedAt}`}
            alt="店舗アイコン画像"
            className={css({
              width: '100%',
              height: 'auto',
              borderRadius: '8px',
            })}
          />
        ) : (
          <div className={css({
            width: '100%',
            height: '200px',
            backgroundColor: 'background.accentSecondary',
            borderRadius: '8px',
          })} />
        )}
      </div>
    </div>
  )
}

const StoreInfoPane = () => {
  const { store, companyId, storeId, isLoading, isError } = useStoreData()
  if (isLoading || isError) {
    return null
  }
  if (!store) {
    return null
  }
  const hasLocation = 
    // #FIXME (0,0)は実在する地点なので、本来は undefined であるべき
    (store.location?.latitude !== undefined && store.location?.latitude !== 0) &&
    (store.location?.longitude !== undefined && store.location?.longitude !== 0)
  const coordindateText = hasLocation ? `${store.location?.latitude?.toFixed(5)}, ${store.location?.longitude?.toFixed(5)}` : "なし"
  const mapLink = `https://www.google.com/maps/search/?api=1&query=${store.location?.latitude},${store.location?.longitude}`

  return (
    <div className={sectionWrapper()}>
      <div className={css({
        position: 'absolute',
        top: '16px',
        right: '24px',
      })}>
        <Link
          to='/admin/companies/$companyId/$storeId/edit'
          params={{
            companyId,
            storeId,
          }}
        >
          <button className={button()} type="button">
            編集
          </button>
        </Link>
      </div>
      <div className={textWithLabelWrapper()}>
        <div className={textLabel()}>店舗名</div>
        <div className={text()}>{store.name}</div>
      </div>
      <div className={textWithLabelWrapper()}>
        <div className={textLabel()}>市区町村</div>
        <div className={text()}>{store.city ?? "なし"}</div>
      </div>
      <div className={textWithLabelWrapper()}>
        <div className={textLabel()}>住所</div>
        <div className={text()}>{store.fullAddress ?? "なし"}</div>
      </div>
      <div className={textWithLabelWrapper()}>
        <div className={textLabel()}>カテゴリ</div>
        <div className={text()}>{store.category}</div>
      </div>
      <div className={textWithLabelWrapper()}>
        <div className={textLabel()}>位置情報</div>
        <div className={text()}>{coordindateText}</div>
        {hasLocation && <div className={text()}>
          <span className={css({
            display: 'flex',
          })}>
            <a
              href={mapLink}
              target='_blank'
              rel='noopener noreferrer'
            >
              <span className={css({
                display: 'inline-flex',
                alignItems: 'center',
                gap: '2px',
                color: 'text.accentPrimary',
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
                width: 'fit-content',
              })}>
                Google マップ
                <OpenInNew />
              </span>
            </a>
          </span>
        </div>}
      </div>
      <div className={textWithLabelWrapper()}>
        <div className={textLabel()}>店舗QRコード識別子</div>
        <div className={text()}>{store.slug}</div>
      </div>
      <div className={textWithLabelWrapper()}>
        <div className={textLabel()}>利用開始日</div>
        <div className={text()}>{store.openedAt ? store.openedAt.toDate().toLocaleDateString() : '未設定'}</div>
      </div>
      <div className={textWithLabelWrapper()}>
        <div className={textLabel()}>利用終了日</div>
        <div className={text()}>{store.closedAt ? store.closedAt.toDate().toLocaleDateString() : '未設定'}</div>
      </div>
    </div>
  )
}
